'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import Image from 'next/image';
import { Link } from '@components/link';
import { formatWholeDollarAmount } from '@utils/money';
import { OtaInfoMap, ComparisonQuoteResult, isSuccessfulComparisonQuote } from '../types';
import { AnalyticsRef } from '@components/analytics-ref';
import { useQuotes } from '../../quotes-context';
import ArrowTrendUp from '../../../../../../../../../public/assets/svg/arrow-trend-up.svg';
import ArrowTrendDown from '../../../../../../../../../public/assets/svg/arrow-trend-down.svg';
type ComparisonQuoteItemProps = {
  comparisonQuote: ComparisonQuoteResult;
  otaUrl: string;
  otaInfo: OtaInfoMap;
  afterTaxes?: boolean;
  isSavvy?: boolean;
  isBestDeal?: boolean;
};
const ComparisonQuoteItem: React.FC<ComparisonQuoteItemProps> = t0 => {
  const $ = _c(24);
  const {
    comparisonQuote,
    otaUrl,
    otaInfo,
    afterTaxes: t1,
    isSavvy: t2,
    isBestDeal: t3
  } = t0;
  const afterTaxes = t1 === undefined ? false : t1;
  const isSavvy = t2 === undefined ? false : t2;
  const isBestDeal = t3 === undefined ? false : t3;
  const {
    quoteData
  } = useQuotes();
  const otaSource = comparisonQuote.externalSource as keyof typeof otaInfo;
  const otaData = otaInfo[otaSource];
  const priceCents = isSuccessfulComparisonQuote(comparisonQuote) ? comparisonQuote.priceCents : undefined;
  const savvyPriceCents = quoteData?.bookingQuoteByListingAndDates.__typename === "BookingQuote" ? afterTaxes ? quoteData.bookingQuoteByListingAndDates.totalRecoverableCostCents : quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents : undefined;
  if (!isSavvy && (!isSuccessfulComparisonQuote(comparisonQuote) || priceCents === undefined)) {
    return null;
  }
  const displayPriceCents = isSavvy ? savvyPriceCents ?? priceCents : priceCents;
  if (displayPriceCents === undefined) {
    return null;
  }
  const priceDifference = savvyPriceCents !== undefined && displayPriceCents !== undefined ? displayPriceCents - savvyPriceCents : 0;
  let t4;
  if ($[0] !== comparisonQuote.externalSource || $[1] !== otaData) {
    t4 = otaData?.icon && <Image src={otaData.icon} alt={comparisonQuote.externalSource ?? ""} width={80} height={32} className="object-contain h-[20px]" />;
    $[0] = comparisonQuote.externalSource;
    $[1] = otaData;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = <div>{t4}</div>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== displayPriceCents) {
    t6 = formatWholeDollarAmount(displayPriceCents);
    $[5] = displayPriceCents;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <p className="text-primary-main font-light text-[16px]">{t6}</p>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isSavvy || $[10] !== priceDifference) {
    t8 = !isSavvy && <div className={`w-10/12 max-w-[100px] text-center py-0.5 rounded-full ${priceDifference === 0 ? "opacity-0" : priceDifference < 0 ? "bg-success-light text-success-dark" : "bg-error-light text-error-main"}`}><p className="font-medium text-[12px] flex items-center justify-center gap-1">{priceDifference > 0 ? <><Image src={ArrowTrendUp} alt="" aria-hidden="true" width={14} height={12} className="w-[14px] h-[12px]" />{formatWholeDollarAmount(priceDifference)} more</> : priceDifference < 0 ? <><Image src={ArrowTrendDown} alt="" aria-hidden="true" width={12} height={10} className="w-[12px] h-[10px]" />{formatWholeDollarAmount(priceDifference)} less</> : "\xA0"}</p></div>;
    $[9] = isSavvy;
    $[10] = priceDifference;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isBestDeal || $[13] !== isSavvy) {
    t9 = isSavvy && <div className={`w-10/12 max-w-[80px] text-center py-0.5 rounded-full ${isBestDeal ? "bg-success-light text-success-main" : "opacity-0"}`}><p className="font-medium text-[12px]">{isBestDeal ? "Best Price" : "\xA0"}</p></div>;
    $[12] = isBestDeal;
    $[13] = isSavvy;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t10 = <div className="flex flex-col w-full items-center justify-center bg-white space-y-2 py-2">{t5}{t7}{t8}{t9}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const cardContent = t10;
  let t11;
  if ($[20] !== afterTaxes || $[21] !== cardContent || $[22] !== otaUrl) {
    t11 = <div className="flex-1"><AnalyticsRef name="ota_quote">{!afterTaxes && otaUrl ? <Link className="block w-full !no-underline" rel="nofollow noopener noreferrer" target="_blank" to={otaUrl}>{cardContent}</Link> : cardContent}</AnalyticsRef></div>;
    $[20] = afterTaxes;
    $[21] = cardContent;
    $[22] = otaUrl;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
export default ComparisonQuoteItem;